/**
 * Editable homepage marketing stats (super-admin managed).
 *
 * A tiny key/value store: each stat carries a free-text `value` ("420+") and a
 * free-text `label` ("Hand-picked hotels"). The set of keys is fixed (6 stats);
 * only their value+label are editable.
 *
 * The storefront ships canonical defaults (SITE_CONTENT_DEFAULTS) and overlays
 * any stored value per key, so a missing key OR a backend outage never breaks
 * the page. The admin also uses the defaults as the "Default" reference shown
 * for keys that have no stored row yet.
 */

export const SITE_CONTENT_KEYS = [
  'hero.hotels',
  'hero.cities',
  'hero.verified',
  'difference.rating',
  'difference.hotels',
  'difference.cities',
] as const;

export type SiteContentKey = (typeof SITE_CONTENT_KEYS)[number];

export interface SiteContentEntry {
  value: string;
  label: string;
}

/** A stored row, as returned by the admin list endpoint. */
export interface SiteContentRow extends SiteContentEntry {
  key: SiteContentKey;
  updatedAt: string;
}

/** Body for the single-key upsert (PUT /v1/admin/site-content). */
export interface UpsertSiteContentRequest extends SiteContentEntry {
  key: SiteContentKey;
}

/** Fully-resolved map — every key present (defaults merged with overrides). */
export type SiteContentMap = Record<SiteContentKey, SiteContentEntry>;

/**
 * Public read shape (GET /v1/public/site-content). Only keys that have a stored
 * row are present — consumers overlay this onto SITE_CONTENT_DEFAULTS.
 */
export type PublicSiteContent = Partial<SiteContentMap>;

/**
 * Canonical fallback copy — the single source of truth shared by the storefront
 * (render fallback) and the admin (the "Default" reference). Values mirror the
 * homepage's original hardcoded marketing stats.
 */
export const SITE_CONTENT_DEFAULTS: SiteContentMap = {
  'hero.hotels': { value: '420+', label: 'Hand-picked hotels' },
  'hero.cities': { value: '60+', label: 'Cities worldwide' },
  'hero.verified': { value: '100%', label: 'Every stay verified' },
  'difference.rating': { value: '4.8', label: 'Average guest rating' },
  'difference.hotels': { value: '500+', label: 'Hand-picked hotels' },
  'difference.cities': { value: '40+', label: 'US cities' },
};
